import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Plane, Hotel, Ship, Package, MapPin, Calendar, 
  Users, ArrowRight, Star, Shield, Sparkles, Globe,
  PhoneCall, Mail, Search, ArrowLeftRight,
  CheckCircle, Headphones, CreditCard, TrendingUp,
  ChevronRight, Heart, Gift
} from 'lucide-react';
import { trackCall } from '../utils/callTracker';
import { useContact } from '../context/ContactContext';
import AirportAutocomplete from '../components/AirportAutocomplete';
import SearchCardNew from '../components/SearchCardNew';
import './HomeNew.css';

function HomeNew() {
  const navigate = useNavigate();
  const { contactSettings } = useContact();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const heroSlides = [
    { image: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80', title: 'Discover Paradise', subtitle: 'Maldives from $899' },
    { image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80', title: 'European Dreams', subtitle: 'Paris from $449' },
    { image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80', title: 'Asian Adventures', subtitle: 'Tokyo from $599' },
  ];

  const trendingDestinations = [
    { city: 'Bali', country: 'Indonesia', price: 849, image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80', rating: 4.9, tag: 'Trending' },
    { city: 'Santorini', country: 'Greece', price: 729, image: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80', rating: 4.8, tag: 'Popular' },
    { city: 'Dubai', country: 'UAE', price: 619, image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80', rating: 4.7, tag: 'Hot Deal' },
    { city: 'Maldives', country: 'South Asia', price: 1199, image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80', rating: 4.9, tag: 'Premium' },
    { city: 'Paris', country: 'France', price: 549, image: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&q=80', rating: 4.8, tag: 'Classic' },
    { city: 'Tokyo', country: 'Japan', price: 699, image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80', rating: 4.9, tag: 'Adventure' },
  ];

  const flightDeals = [
    { from: 'New York', fromCode: 'JFK', to: 'London', toCode: 'LHR', price: 389, airline: 'British Airways', duration: '7h 15m', stops: 'Nonstop' },
    { from: 'Los Angeles', fromCode: 'LAX', to: 'Tokyo', toCode: 'NRT', price: 549, airline: 'Japan Airlines', duration: '11h 30m', stops: 'Nonstop' },
    { from: 'Chicago', fromCode: 'ORD', to: 'Paris', toCode: 'CDG', price: 429, airline: 'Air France', duration: '8h 45m', stops: 'Nonstop' },
    { from: 'Miami', fromCode: 'MIA', to: 'Cancun', toCode: 'CUN', price: 199, airline: 'American Airlines', duration: '1h 45m', stops: 'Nonstop' },
  ];

  const testimonials = [
    { name: 'Sarah Mitchell', role: 'Frequent Traveler', text: 'Flight Reservation Centre saved me over $500 on my Europe trip! The booking process was seamless and their customer support is available 24/7.', rating: 5, image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80' },
    { name: 'James Rodriguez', role: 'Business Traveler', text: "Best travel platform I've used. The mobile app makes managing bookings on-the-go incredibly easy. Highly recommend!", rating: 5, image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80' },
    { name: 'Emily Chen', role: 'Adventure Seeker', text: 'Found amazing deals to destinations I never thought I could afford. Their price match guarantee gives me confidence every time.', rating: 5, image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80' },
  ];

  const features = [
    { icon: Shield, title: 'Best Price Guarantee', desc: 'Find a lower price? We\'ll match it and give you 10% off.', color: '#10b981' },
    { icon: Headphones, title: '24/7 Expert Support', desc: 'Our travel experts are available around the clock to help you.', color: '#6366f1' },
    { icon: CreditCard, title: 'Secure Payments', desc: 'Your transactions are protected with bank-level security.', color: '#f59e0b' },
    { icon: Gift, title: 'Exclusive Rewards', desc: 'Earn points on every booking and unlock special perks.', color: '#ec4899' },
  ];

  const [stats, setStats] = useState({ bookings: 0, customers: 0, destinations: 0, savings: 0 });

  // Auto-slide hero
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // Stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !statsVisible) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [statsVisible]);

  useEffect(() => {
    if (!statsVisible) return;
    const targets = { bookings: 150000, customers: 50000, destinations: 500, savings: 25 };
    const duration = 2000; const steps = 60; const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setStats({
        bookings: Math.floor((targets.bookings / steps) * step),
        customers: Math.floor((targets.customers / steps) * step),
        destinations: Math.floor((targets.destinations / steps) * step),
        savings: Math.floor((targets.savings / steps) * step)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [statsVisible]);

  // Scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('visible'); });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    document.querySelectorAll('.fade-up').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSearch = async (searchParams) => {
    const { from, to, departDate, returnDate, tripType, passengers, cabinClass } = searchParams;
    
    console.log("🔍 Search initiated with params:", searchParams);

    try {
      // Calculate total passengers
      const totalPassengers = passengers.adults + passengers.children + passengers.infants;

      // Build query parameters
      const queryParams = new URLSearchParams({
        from: from,
        to: to,
        date: departDate,
        passengers: totalPassengers.toString(),
        tripType: tripType === 'round-trip' ? 'roundtrip' : tripType === 'one-way' ? 'oneway' : 'multicity',
      });

      // Add return date for round trips
      if (tripType === 'round-trip' && returnDate) {
        queryParams.append('returnDate', returnDate);
      }

      // Add cabin class if not economy
      if (cabinClass && cabinClass !== 'economy') {
        queryParams.append('cabinClass', cabinClass);
      }

      console.log("📡 Calling API:", `/api/flights?${queryParams.toString()}`);

      // Call backend API
      const response = await fetch(`/api/flights?${queryParams.toString()}`);
      const data = await response.json();

      console.log("✅ API Response:", data);

      if (data.success && data.data && data.data.length > 0) {
        console.log(`🎉 Found ${data.count} flights from ${data.sources.join(', ')}`);
        console.log("Flight Results:", data.data);
        
        // Navigate to results page with data
        navigate('/flight-results', { 
          state: { 
            searchParams: searchParams,
            flights: data.data,
            sources: data.sources
          } 
        });
      } else {
        console.warn("⚠️ No flights found");
        alert("No flights found for this route. Please try different dates or airports.");
      }
    } catch (error) {
      console.error("❌ Error searching flights:", error);
      alert("Error searching flights. Please try again.");
    }
  };

  const tfn = contactSettings.tfn || '+1-888-859-0441';

  return (
    <>
      <Helmet>
        <title>Flight Reservation Centre - Cheap Flights & Travel Deals | America's #1 Travel Platform</title>
        <meta name="description" content="Book cheap flights, hotels, cruises & vacation packages. Save up to 40% on travel deals. Compare prices across 500+ airlines. 24/7 expert support. Call now!" />
        <meta name="keywords" content="cheap flights, airfare deals, flight booking, travel deals, vacation packages, hotels, cruises, airline tickets, discount flights, flightreservationcentre" />
        <link rel="canonical" href="https://flightreservationcentre.com/" />
        <meta property="og:title" content="Flight Reservation Centre - Cheap Flights & Travel Deals" />
        <meta property="og:description" content="America's #1 Travel Booking Platform. Book cheap flights, hotels, cruises and vacation packages. Save up to 40%!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flightreservationcentre.com/" />
        <meta property="og:image" content="https://flightreservationcentre.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Flight Reservation Centre - Cheap Flights & Travel Deals" />
        <meta name="twitter:description" content="America's #1 Travel Booking Platform. Book cheap flights, hotels, cruises and vacation packages." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            "name": "Flight Reservation Centre",
            "url": "https://flightreservationcentre.com",
            "logo": "https://flightreservationcentre.com/logo.svg",
            "description": "America's #1 Travel Booking Platform for cheap flights, hotels, cruises and vacation packages.",
            "telephone": contactSettings.tfn || "+1-888-859-0441",
            "priceRange": "$",
            "address": { "@type": "PostalAddress", "addressCountry": "US" }
          })}
        </script> 
      </Helmet>

      <div className="">
        {/* Floating Call Button */}
        <a href={`tel:${tfn.replace(/[^0-9+]/g, '')}`} className="floating-cta" onClick={() => trackCall('/home', 'floating-cta')}>
          <div className="pulse-ring"></div>
          <PhoneCall size={24} />
          <div className="cta-text">
            <span className="cta-label">Call Now</span>
            <span className="cta-number">{tfn}</span>
          </div>
        </a>

        {/* ===== HERO SECTION ===== */}
        <section className="hero-modern">
          <div className="hero-slider">
            {heroSlides.map((slide, idx) => (
              <div key={idx} className={`hero-slide ${idx === currentSlide ? 'active' : ''}`}>
                <img src={slide.image} alt={slide.title} />
                <div className="slide-overlay"></div>
              </div>
            ))}
          </div>
          
          <div className="hero-content">
            <div className="container">
              <div className="hero-text fade-up">
                <div className="hero-badge">
                  <Sparkles size={16} />
                  <span>🇺🇸 America's #1 Travel Platform</span>
                </div>
                <h1>Explore the World<br/><span className="gradient-text">Your Way</span></h1>
                <p>Compare prices across 500+ airlines and save up to 40% on your next adventure. Trusted by over 2 million travelers.</p>
                
                <div className="hero-stats-inline">
                  <div className="stat-inline">
                    <CheckCircle size={18} />
                    <span>Best Price Guarantee</span>
                  </div>
                  <div className="stat-inline">
                    <Shield size={18} />
                    <span>Free Cancellation</span>
                  </div>
                  <div className="stat-inline">
                    <Headphones size={18} />
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>

              {/* New Search Card */}
              <SearchCardNew onSearch={handleSearch} />
            </div>
          </div>

        </section>

      

        {/* ===== TRENDING DESTINATIONS ===== */}
        <section className="destinations-section">
          <div className="container">
            <div className="section-header fade-up">
              <div>
                <h2>Trending Destinations</h2>
                <p>Most popular places to visit this season</p>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/packages')}>
                View All <ChevronRight size={18} />
              </button>
            </div>

            <div className="destinations-grid fade-up">
              {trendingDestinations.map((dest, idx) => (
                <div key={idx} className="destination-card" onClick={() => navigate('/package-results')}>
                  <div className="card-image">
                    <img src={dest.image} alt={dest.city} loading="lazy" />
                    <div className="card-overlay">
                      <span className="dest-tag">{dest.tag}</span>
                      <button className="wishlist-btn"><Heart size={18} /></button>
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="dest-info">
                      <h3>{dest.city}</h3>
                      <p>{dest.country}</p>
                    </div>
                    <div className="dest-meta">
                      <div className="dest-rating">
                        <Star size={14} fill="#f59e0b" stroke="#f59e0b" />
                        <span>{dest.rating}</span>
                      </div>
                      <div className="dest-price">
                        <span className="price-label">from</span>
                        <span className="price-value">${dest.price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FLIGHT DEALS ===== */}
        <section className="deals-section">
          <div className="container">
            <div className="section-header fade-up">
              <div>
                <h2>Today's Best Flight Deals</h2>
                <p>Grab these amazing prices before they're gone</p>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/flights')}>
                View All <ChevronRight size={18} />
              </button>
            </div>

            <div className="deals-list fade-up">
              {flightDeals.map((deal, idx) => (
                <div key={idx} className="deal-card" onClick={() => navigate('/flight-results')}>
                  <div className="deal-route">
                    <div className="route-point">
                      <span className="city-code">{deal.fromCode}</span>
                      <span className="city-name">{deal.from}</span>
                    </div>
                    <div className="route-line">
                      <Plane size={18} />
                      <span>{deal.duration}</span>
                    </div>
                    <div className="route-point">
                      <span className="city-code">{deal.toCode}</span>
                      <span className="city-name">{deal.to}</span>
                    </div>
                  </div>
                  <div className="deal-info">
                    <span className="airline">{deal.airline}</span>
                    <span className="stops">{deal.stops}</span>
                  </div>
                  <div className="deal-price">
                    <span className="price-from">from</span>
                    <span className="price">${deal.price}</span>
                    <span className="price-pp">per person</span>
                  </div>
                  <button className="deal-btn">
                    Book Now <ArrowRight size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FEATURES SECTION ===== */}
        <section className="features-section">
          <div className="container">
            <div className="section-header centered fade-up">
              <h2>Why Choose Flight Reservation Centre?</h2>
              <p>We make travel booking simple, affordable, and enjoyable</p>
            </div>

            <div className="features-grid fade-up">
              {features.map((feature, idx) => (
                <div key={idx} className="feature-card">
                  <div className="feature-icon" style={{ backgroundColor: `${feature.color}15`, color: feature.color }}>
                    <feature.icon size={28} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

          {/* ===== STATS SECTION ===== */}
                <section className="stats-section" ref={statsRef}>
                  <div className="container">
                    <div className="stats-grid">
                      <div className="stat-item">
                        <div className="stat-icon"><Globe size={32} /></div>
                        <h3>{stats.destinations.toLocaleString()}+</h3>
                        <p>Destinations</p>
                      </div>
                      <div className="stat-item">
                        <div className="stat-icon"><Users size={32} /></div>
                        <h3>{stats.customers.toLocaleString()}+</h3>
                        <p>Happy Travelers</p>
                      </div>
                      <div className="stat-item">
                        <div className="stat-icon"><Plane size={32} /></div>
                        <h3>{stats.bookings.toLocaleString()}+</h3>
                        <p>Flights Booked</p>
                      </div>
                      <div className="stat-item">
                        <div className="stat-icon"><TrendingUp size={32} /></div>
                        <h3>${stats.savings}M+</h3>
                        <p>Saved by Travelers</p>
                      </div>
                    </div>
                  </div>
                </section>
        
                {/* ===== TESTIMONIALS ===== */}
                <section className="testimonials-section">
                  <div className="container">
                    <div className="section-header centered fade-up">
                      <h2>What Our Travelers Say</h2>
                      <p>Real experiences from real customers</p>
                    </div>
        
                    <div className="testimonials-grid fade-up">
                      {testimonials.map((t, idx) => (
                        <div key={idx} className="testimonial-card">
                          <div className="quote-mark">"</div>
                          <p className="testimonial-text">{t.text}</p>
                          <div className="testimonial-rating">
                            {[...Array(t.rating)].map((_, i) => (
                              <Star key={i} size={16} fill="#f59e0b" stroke="#f59e0b" />
                            ))}
                          </div>
                          <div className="testimonial-author">
                            <img src={t.image} alt={t.name} />
                            <div>
                              <h4>{t.name}</h4>
                              <span>{t.role}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
        
                {/* ===== NEWSLETTER ===== */}
                <section className="newsletter-section fade-up">
                  <div className="container">
                    <div className="newsletter-card">
                      <div className="newsletter-content">
                        <h2>Get Exclusive Travel Deals</h2>
                        <p>Subscribe to our newsletter and receive up to 50% off your first booking!</p>
                        <form className="newsletter-form" onSubmit={(e) => { e.preventDefault(); alert('Thank you for subscribing!'); }}>
                          <div className="input-wrapper">
                            <Mail size={20} />
                            <input type="email" placeholder="Enter your email address" required />
                          </div>
                          <button type="submit">Subscribe <ArrowRight size={18} /></button>
                        </form>
                        <span className="privacy-note">We respect your privacy. Unsubscribe anytime.</span>
                      </div>
                      <div className="newsletter-image">
                        <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&q=80" alt="Travel" />
                      </div>
                    </div>
                  </div>
                </section>
        
                {/* ===== FINAL CTA ===== */}
                <section className="final-cta">
                  <div className="container">
                    <div className="cta-content fade-up">
                      <h2>Ready to Start Your Adventure?</h2>
                      <p>Our travel experts are available 24/7 to help you plan the perfect trip</p>
                      <div className="cta-buttons">
                        <a href={`tel:${tfn.replace(/[^0-9+]/g, '')}`} className="cta-btn primary" onClick={() => trackCall('/home', 'hero-cta')}>
                          <PhoneCall size={22} />
                          <div>
                            <span className="btn-label">Call to Book</span>
                            <span className="btn-phone">{tfn}</span>
                          </div>
                        </a>
                        <a href={`mailto:${contactSettings.email || 'support@flightreservationcentre.com'}`} className="cta-btn secondary">
                          <Mail size={22} />
                          <span>Email Us</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </section>


      </div>
    </>
  );
}

export default HomeNew;
